/* eslint-disable new-cap */
/**
 * REST API を使用したメール詳細取得 hook
 * OpenAPI仕様から生成された型を使用
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import type { EmailDocument, RecruitmentType } from "./useEmailsRestApi";

// =====================================
// REST API Hook Types
// =====================================

export interface EmailDetailResponse {
  email: EmailDocument;
}

export interface UseEmailDetailRestApiOptions {
  variables: {
    id: string;
  };
  skip?: boolean;
}

export interface UseEmailDetailRestApiResult {
  data: EmailDetailResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<EmailDetailResponse>;
}

/**
 * メール詳細を REST API から取得する hook
 * @param {UseEmailDetailRestApiOptions} options - フック設定オプション
 * @return {UseEmailDetailRestApiResult} メール詳細データ、エラー、ローディング状態、再取得関数を含むオブジェクト
 */
export function useEmailDetailRestApi(
  options: UseEmailDetailRestApiOptions,
): UseEmailDetailRestApiResult {
  const [data, setData] = useState<EmailDetailResponse | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchEmailDetail = useCallback(
    async (id: string): Promise<EmailDetailResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const { data: responseData, error: apiError } = await api.GET(
          "/api/emails/{id}",
          {
            params: {
              path: { id },
            },
          },
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || "API error",
          );
        }

        if (!responseData) {
          throw new Error("No data received");
        }

        const result: EmailDetailResponse = {
          email: {
            ...responseData,
            recruitmentType: responseData.recruitmentType as RecruitmentType,
          },
        };

        setData(result);
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (options.skip || !options.variables.id) {
      setLoading(false);
      return;
    }

    fetchEmailDetail(options.variables.id).catch(() => {
      // エラーは state で管理
    });
  }, [options.skip, options.variables.id, fetchEmailDetail]);

  const refetch = useCallback(async (): Promise<EmailDetailResponse> => {
    return fetchEmailDetail(options.variables.id);
  }, [fetchEmailDetail, options.variables.id]);

  return {
    data,
    error,
    loading,
    refetch,
  };
}

export default useEmailDetailRestApi;
